<script module lang="ts">
</script>

<script lang="ts">
    import { onMount, onDestroy } from "svelte"
    import { _ } from "svelte-i18n"
    import inputfields from "./inputfields"
    import { RuleEngine } from "./RuleEngine"
    import { showOfferAssistant } from "../../../functionTools/offerAssistant"
    import { getOfferAssistantConfig, postOfferAssistantTrackingEvent } from "../../../api"
    import BarLoader from "svelte-loading-spinners/BarLoader.svelte"
    import { normalizeValueObject, prepareSelectionsForDealers } from "./utils"
    import { overlay } from "../../../store"
    import Modal from "../../widgets/Modal.svelte"
    import { inquiry } from "../../../functionTools/offerAssistant"
    import { iframeView, preselectedDealerNumber } from "../../../functionTools/iframeIntegration"

    interface Props {
        currentChoices?: {
            [context: string]: {
                code: number
                label: string
                labelLong?: string
                onSelect?: Function
            }
        }
        id: string
        ftpAssetsUrl: string
        configUrl?: string
        show?: boolean
        value?: any
        reset?: boolean
        submitInquirySuccessCallback: () => void
    }

    let {
        currentChoices = {},
        id,
        ftpAssetsUrl,
        configUrl = null,
        show = $bindable(true),
        value = $bindable(null),
        reset = $bindable(false),
        submitInquirySuccessCallback,
    }: Props = $props()
    currentChoices = {}
    let mappedCurrentChoices: { [key: string]: any } = $state({})
    let propertyMappings: { [key: string]: string } = {
        BK_Anzahl_Briefkasten: "Weh",
    }
    for (const prop in currentChoices) {
        if (propertyMappings[prop]) {
            mappedCurrentChoices[propertyMappings[prop] as string] = (currentChoices[prop] as any).code
        }
    }

    let showWelcomeModal: boolean = $state(true)
    let configuration: OfferAssistantField[] = $state()
    let configurationName: string
    let selected: string = $state()
    let stepsFinallyCompleted: { [key: string]: boolean } = $state({})
    let pending: boolean = $state(true)
    let configLoadingFailed: boolean = $state(false)
    let selectionInformationForDealers: string = ""
    let startTracked = $state(false)

    const ruleEngine = new RuleEngine()
    const normalizedConfiguration: {
        configuration: ConfigOption[]
    } = {
        configuration: [],
    }

    if (!value) value = (typeof window !== "undefined" && JSON.parse(window.localStorage.getItem(id))) || {}

    onMount(async () => {
        configLoadingFailed = false
        if (configUrl) getConfiguration("mailboxes")
    })

    onDestroy(() => ($showOfferAssistant = false))

    const getTrackingData = () => ({
        configurationName: configurationName || id,
        customerType: $inquiry.customerType || undefined,
        iframe: $iframeView,
        preselectedDealer: !!$preselectedDealerNumber,
        dealerSelected: !!$inquiry.dealerChoice?.number,
        countryCode: $inquiry.billingAddress?.countryCode || undefined,
    })

    const getConfiguration = async (configName: string) => {
        try {
            pending = true
            configLoadingFailed = false
            const response = await getOfferAssistantConfig(configName)
            const configData = response.data[0]
            configurationName = configData.config.title
            configuration = configData.config.steps
            selected = configuration[0].name
            if (typeof window !== "undefined") {
                const lastConfigVersion = window.localStorage.getItem("offer-assistant-config-version")
                console.log(
                    "lastConfigVersion:",
                    lastConfigVersion,
                    "response.data[0].updateTime:",
                    configData.updateTime
                )
                if (lastConfigVersion !== configData.updateTime) {
                    window.localStorage.setItem("offer-assistant-config-version", configData.updateTime)
                    value = {}
                    resetOfferAssistant()
                    showWelcomeModal = true
                }
            }
            if (!startTracked) {
                startTracked = true
                postOfferAssistantTrackingEvent("offerAssistantStart", getTrackingData()).catch((error) => {
                    console.error("Failed to post offer assistant start event", error)
                })
            }
        } catch (error) {
            console.error("Error loading configuration:", error)
            configLoadingFailed = true
        } finally {
            pending = false
        }
    }

    const hasPrev = (): boolean => {
        const currentIndex = configuration.findIndex((step) => step.name === selected)
        return currentIndex > 0
    }

    const hasNext = (): boolean => {
        const currentIndex = configuration.findIndex((step) => step.name === selected)
        return currentIndex >= 0 && currentIndex < configuration.length - 1
    }

    const onBack = () => {
        const currentIndex = configuration.findIndex((step) => step.name === selected)
        if (currentIndex >= 0) {
            const prevStepIndex = findPreviousDisplayedStepIndex(currentIndex)
            selected = configuration[prevStepIndex].name
        }
    }

    const findPreviousDisplayedStepIndex = (stepIndex: number): number => {
        for (let index = stepIndex - 1; index >= 0; index--) {
            const step = configuration[index]
            if (!step.dependsOn || ruleEngine.resolve(step.dependsOn, value)) return index
        }
        return 0
    }

    const onNext = () => {
        const currentIndex = configuration.findIndex((step) => step.name === selected)
        if (currentIndex >= 0) {
            const nextStepIndex = findNextDisplayedStepIndex(currentIndex)
            selected = configuration[nextStepIndex].name
        }
    }

    const findNextDisplayedStepIndex = (stepIndex: number): number => {
        for (let i = stepIndex + 1; i < configuration.length; i++) {
            if (!configuration[i].dependsOn || ruleEngine.resolve(configuration[i].dependsOn, value)) {
                return i
            }
        }
        return stepIndex
    }

    const removeSelections = (v: any) => {
        for (var prop in v) {
            if (typeof v[prop] === "object") removeSelections(v[prop])
            else if (Array.isArray(v[prop])) v[prop] = []
            else v[prop] = null
        }
        value = value
    }

    const isFinallyCompleted = (): boolean => {
        let completed: boolean = true
        configuration.forEach((field) => {
            if (ruleEngine.resolve(field.dependsOn, value) && !stepsFinallyCompleted[field.name]) {
                completed = false
            }
        })

        return completed
    }

    const sendInquiry = () => {
        normalizedConfiguration.configuration = []
        normalizeValueObject(value, normalizedConfiguration)
        selectionInformationForDealers = prepareSelectionsForDealers(configuration, value)
        postOfferAssistantTrackingEvent("offerAssistantInquiryOpen", getTrackingData()).catch((error) => {
            console.error("Failed to post offer assistant inquiry open event", error)
        })

        $overlay = {
            show: true,
            type: "inquiry",
            properties: {
                offerAssistantConfiguration: {
                    id,
                    configurationName: configurationName,
                    dealerInformation: selectionInformationForDealers,
                    configuration: normalizedConfiguration.configuration,
                },
                submitInquirySuccessCallback,
            },
        }
    }

    const resetOfferAssistant = () => {
        removeSelections(value)
        if (typeof window !== "undefined") window.localStorage.removeItem(id)
        reset = false
        if (configuration) selected = configuration[0].name
    }

    $effect(() => {
        if (reset === true) resetOfferAssistant()
        else if (typeof window !== "undefined") window.localStorage.setItem(id, JSON.stringify(value))
    })
</script>

{#if configLoadingFailed}
    <Modal show={configLoadingFailed} size="sm">
        {#snippet header()}
            <div>{$_("econfig.error")}</div>
        {/snippet}
        <p>
            {$_("econfig.error-message")}
        </p>
        {#snippet footer()}
            <div>
                <button class="button" onclick={() => (show = false)}>Zurück zum Konfigurator</button>
            </div>
        {/snippet}
    </Modal>
{:else if pending || !configuration}
    <BarLoader />
{:else if show && configuration}
    <div class="offer-assistent-component">
        <div class="offer-assistent">
            {#if configuration}
                {#each configuration as field}
                    {@const SvelteComponent =
                        inputfields[field.widget as keyof typeof inputfields] || inputfields.default}
                    <SvelteComponent
                        {...field}
                        bind:value={value[field.name]}
                        bind:selected
                        values={value}
                        onCompleted={(completed) => {
                            stepsFinallyCompleted[field.name] = completed
                        }}
                        currentChoices={mappedCurrentChoices}
                        {ftpAssetsUrl}
                    />
                {/each}
            {/if}
        </div>
        {#key selected}
            {#key stepsFinallyCompleted}
                <div class="offer-assistent-actions">
                    <div>
                        {#if value}
                            <button
                                class="button secondary"
                                onclick={() => {
                                    removeSelections(value)
                                    if (typeof window !== "undefined") {
                                        window.localStorage.removeItem(id)
                                    }
                                }}>{$_("econfig.delete-filter")}</button
                            >
                        {/if}
                    </div>
                    {#if isFinallyCompleted()}
                        <button class="button primary" onclick={sendInquiry}>
                            {$_("product-config.make-inquiry")}
                        </button>
                    {:else}
                        <div class="offer-assistent-navigation">
                            <button class="button primary" disabled={!hasPrev()} onclick={onBack}>Zurück</button>
                            <button class="button primary" disabled={!hasNext()} onclick={onNext}>Weiter</button>
                        </div>
                    {/if}
                </div>
            {/key}
        {/key}
    </div>
{/if}

<Modal show={showWelcomeModal && !configLoadingFailed} size="md">
    {#snippet header()}
        <div>
            {$_("offer-assistant.welcome-title")}
        </div>
    {/snippet}
    <p>
        {@html $_("offer-assistant.welcome-text")}
    </p>
    {#snippet footer()}
        <div>
            <button class="button primary" onclick={() => (showWelcomeModal = false)}>
                {$_("offer-assistant.ok")}
            </button>
        </div>
    {/snippet}
</Modal>
